import React from 'react';
import { useT } from '../../i18n/useT';
import { Helmet } from 'react-helmet-async';
import LegalPage, { LegalSection, LegalPlaceholder, LegalNote } from '../../components/legal/LegalPage';
import '../../components/legal/LegalPage.css';

export default function TermsPage() {
  const t = useT();
  const lp = t.legalPages;
  return (
    <>
      <Helmet>
        <title>{lp.terms.title} – Docudi</title>
        <meta name="description" content={lp.terms.metaDesc} />
        <meta name="robots" content="noindex" />
      </Helmet>

      <LegalPage title={lp.terms.title} lastUpdated="[PLACEHOLDER: Date]">

        <LegalSection id="acceptance" title="1. Acceptance of Terms">
          <div className="legal-section__content">
            <p>
              By accessing or using Docudi ("the Service"), operated by <LegalPlaceholder label="Company name" />
              ("<LegalPlaceholder label="Company name / we / us" />"), you agree to be bound by these Terms and
              Conditions ("Terms"). If you do not agree to these Terms, please do not use the Service.
            </p>
            <p>
              We reserve the right to modify these Terms at any time. Material changes will be communicated via
              email or in-app notification. Continued use of the Service after such changes constitutes your
              acceptance of the updated Terms.
            </p>
          </div>
        </LegalSection>

        <LegalSection id="service-description" title="2. Description of Service">
          <div className="legal-section__content">
            <p>
              Docudi is a document scanning, storage, and organisation application available on
              <LegalPlaceholder label="iOS / Android / Web — list your actual platforms" />. The Service allows
              users to capture, scan, organise, and export documents.
            </p>
            <p>
              We provide the Service on a subscription basis. Specific features available to each subscription tier
              are described on our <a href="/pricing">Pricing page</a>.
            </p>
            <LegalNote>
              Describe the service accurately — these terms must reflect what you actually provide.
            </LegalNote>
          </div>
        </LegalSection>

        <LegalSection id="accounts" title="3. User Accounts">
          <div className="legal-section__content">
            <ul>
              <li>You must be at least <LegalPlaceholder label="13 / 16 / 18 — verify by jurisdiction" /> years old to create an account.</li>
              <li>You are responsible for maintaining the security of your account credentials.</li>
              <li>You must provide accurate and current information when registering.</li>
              <li>You may not create accounts for others without their consent.</li>
              <li>You may not share your account credentials with third parties.</li>
              <li>You are responsible for all activity that occurs under your account.</li>
            </ul>
            <p>
              We reserve the right to suspend or terminate accounts that violate these Terms.
            </p>
          </div>
        </LegalSection>

        <LegalSection id="acceptable-use" title="4. Acceptable Use">
          <div className="legal-section__content">
            <p>You agree not to use the Service to:</p>
            <ul>
              <li>Upload, store, or transmit illegal content, including but not limited to child sexual abuse material (CSAM).</li>
              <li>Infringe the intellectual property rights of others.</li>
              <li>Attempt to gain unauthorised access to the Service or other users' accounts.</li>
              <li>Introduce malware, viruses, or other malicious code.</li>
              <li>Reverse-engineer, decompile, or disassemble the Service.</li>
              <li>Use the Service for commercial spamming or unsolicited communications.</li>
              <li>Circumvent any rate limits or security measures.</li>
            </ul>
          </div>
        </LegalSection>

        <LegalSection id="payment" title="5. Payment & Subscriptions">
          <div className="legal-section__content">
            <p>
              Paid plans are billed in advance on a monthly or annual basis. All prices are listed in
              <LegalPlaceholder label="EUR / USD" /> and are <LegalPlaceholder label="inclusive / exclusive" /> of
              applicable taxes. <LegalPlaceholder label="Describe VAT handling — e.g. 'EU VAT may apply based on your location'" />.
            </p>
            <p>
              Payments are processed by <LegalPlaceholder label="Stripe / payment provider" />. We do not store
              your full card details.
            </p>
            <p>
              Subscriptions renew automatically unless cancelled before the renewal date. You may cancel at
              any time via your account settings or by contacting us at <LegalPlaceholder label="billing@example.com" />.
            </p>
            <p>
              <LegalPlaceholder label="Describe your refund policy — e.g. '14-day money-back guarantee' or 'no refunds on partial months'" />.
            </p>
            <p>
              We reserve the right to change pricing with <LegalPlaceholder label="X days" /> prior written notice.
            </p>
          </div>
        </LegalSection>

        <LegalSection id="cancellation" title="6. Cancellation & Downgrade">
          <div className="legal-section__content">
            <p>
              You may cancel or downgrade your subscription at any time. Cancellation takes effect at the end
              of the current billing period. During this period you retain access to paid features.
            </p>
            <p>
              Upon cancellation, your account reverts to the free tier. Data stored within free-tier limits
              is retained. Data exceeding free-tier limits will be <LegalPlaceholder label="describe data handling — e.g. 'read-only / flagged for deletion after 30 days'" />.
            </p>
          </div>
        </LegalSection>

        <LegalSection id="ip" title="7. Intellectual Property">
          <div className="legal-section__content">
            <p>
              The Service, including its design, code, logos, and branding, is owned by
              <LegalPlaceholder label="Company name" /> and protected by copyright, trademark, and other laws.
              You may not copy, reproduce, or create derivative works of the Service without prior written consent.
            </p>
            <p>
              <strong>Your content:</strong> You retain full ownership of all documents and data you upload to
              Docudi. By using the Service, you grant us a limited, non-exclusive, royalty-free licence to store
              and process your content solely for the purpose of providing the Service to you.
            </p>
          </div>
        </LegalSection>

        <LegalSection id="liability" title="8. Limitation of Liability">
          <div className="legal-section__content">
            <p>
              To the maximum extent permitted by applicable law, <LegalPlaceholder label="Company name" /> shall
              not be liable for any indirect, incidental, special, consequential, or punitive damages arising from
              your use of the Service.
            </p>
            <p>
              Our total aggregate liability shall not exceed the greater of (a) the amount you paid us in the
              12 months preceding the event giving rise to liability, or (b) <LegalPlaceholder label="€100 / minimum amount" />.
            </p>
            <LegalNote>
              Liability limitation clauses may not be enforceable in all jurisdictions (e.g. consumer protection
              laws in Germany may restrict them). Have a lawyer review this section.
            </LegalNote>
          </div>
        </LegalSection>

        <LegalSection id="disclaimers" title="9. Disclaimers">
          <div className="legal-section__content">
            <p>
              The Service is provided "as is" and "as available" without warranties of any kind, express or
              implied, including fitness for a particular purpose or non-infringement.
            </p>
            <p>
              We do not guarantee that the Service will be uninterrupted, error-free, or free of viruses.
              We are not responsible for the content of documents you store.
            </p>
          </div>
        </LegalSection>

        <LegalSection id="governing-law" title="10. Governing Law & Jurisdiction">
          <div className="legal-section__content">
            <p>
              These Terms are governed by and construed in accordance with the laws of
              <LegalPlaceholder label="Country / State — e.g. Germany / England & Wales" />, without regard to
              conflict of law provisions.
            </p>
            <p>
              Any disputes arising from these Terms shall be subject to the exclusive jurisdiction of the
              courts of <LegalPlaceholder label="City, Country" />.
            </p>
            <p>
              <LegalPlaceholder label="If applicable: EU consumer law may give consumers in their home country additional rights regardless of this clause." />
            </p>
          </div>
        </LegalSection>

        <LegalSection id="contact" title="11. Contact">
          <div className="legal-section__content">
            <p>
              For any questions about these Terms, contact:<br />
              <LegalPlaceholder label="Company name" /><br />
              <LegalPlaceholder label="Address" /><br />
              Email: <LegalPlaceholder label="legal@example.com" />
            </p>
          </div>
        </LegalSection>

      </LegalPage>
    </>
  );
}
